import React, { useCallback, useMemo, useState } from 'react';
import {
  Collapse,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';
import { OrderedMap, Set, List } from 'immutable';
import { CodeViewer } from './CodeViewer';
import { Icon } from './Icon';

// Source: https://davidwalsh.name/javascript-download
const downloadCertificate = (
  certificate,
  filename = 'certificate.crt',
) => () => {
  // Create an invisible A element
  const a = document.createElement('a');
  a.style.display = 'none';
  document.body.appendChild(a);

  // Set the HREF to a Blob representation of the data to be downloaded
  a.href = window.URL.createObjectURL(
    new Blob([certificate], { type: 'plain/text' }),
  );

  // Use download attribute to set set desired file name
  a.setAttribute('download', `${filename}`);

  // Trigger the download by simulating click
  a.click();

  // Cleanup
  window.URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
};

// Helper for parsing the subject, issuer, and fingerprints objects which takes
// a defaults map for defining pretty labels for the properties, and returns a
// list of objects with labels and values to display.
const parseCertificateParts = (parts, defaultsMap) => {
  if (parts && parts.size > 0) {
    return Set([...defaultsMap.keySeq().toJS(), ...parts.keySeq().toJS()])
      .toJS()
      .map(key => {
        return parts.has(key)
          ? {
              label: defaultsMap.get(key, key),
              value: List.isList(parts.get(key))
                ? parts.get(key).join('\n')
                : parts.get(key),
              key,
            }
          : null;
      })
      .filter(Boolean);
  }
  return null;
};
// Default mapping for labels of certificate subject and issuer parts
const distinguishedNameParts = OrderedMap({
  commonName: 'Common Name (CN)',
  alternativeNames: 'Alternative Names',
  organization: 'Organization (O)',
  organizationalUnit: 'Organizational Unit (OU)',
  locality: 'Locality (L)',
  stateOrProvince: 'State/Province (ST)',
  country: 'Country (C)',
  email: 'Email (EMAIL)',
});
// Default mapping for labels of certificate fingerprints
const fingerprintParts = OrderedMap({
  sha256: 'SHA-256',
  sha1: 'SHA-1',
});

const CertificateMetadata = ({ certificate }) => {
  const [dates, subject, issuer, fingerprints] = useMemo(
    () => [
      [
        {
          key: 'validFrom',
          label: 'Issued',
          value: certificate.get('validFrom'),
        },
        {
          key: 'validTo',
          label: 'Expires',
          value: certificate.get('validTo'),
        },
      ].filter(d => !!d.value),
      parseCertificateParts(certificate.get('subject'), distinguishedNameParts),
      parseCertificateParts(certificate.get('issuer'), distinguishedNameParts),
      parseCertificateParts(certificate.get('fingerprints'), fingerprintParts),
    ],
    [certificate],
  );

  return (
    <div className="card card--metadata card-sm">
      <dl className="metadata">
        {dates.map(({ key, label, value }, i) => (
          <div key={key}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
        {subject && (
          <>
            <div className="meta-header">
              <dt>Subject</dt>
            </div>
            {subject.map(({ key, label, value }, i) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </>
        )}
        {issuer && (
          <>
            <div className="meta-header">
              <dt>Issuer</dt>
            </div>
            {issuer.map(({ key, label, value }, i) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </>
        )}
        {fingerprints && (
          <>
            <div className="meta-header">
              <dt>Fingerprints</dt>
            </div>
            {fingerprints.map(({ key, label, value }, i) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </>
        )}
      </dl>
    </div>
  );
};

const CertificateChainMetadata = ({ chain }) => {
  const [chainIndex, setChainIndex] = useState(null);
  const toggleChain = useCallback(
    index => () => setChainIndex(i => (i === index ? null : index)),
    [],
  );

  if (!chain || chain.size === 0) {
    return null;
  }

  return (
    <>
      <div className="heading heading-sm mt-4">
        <div className="h4">CA Chain</div>
      </div>
      {chain.map((c, i) => (
        <div className="mt-1" key={`chain-${i}`}>
          <button
            className="btn btn-link-primary ps-0"
            onClick={toggleChain(i)}
          >
            <Icon name={chainIndex === i ? 'chevron-down' : 'chevron-right'} />
            <span className="text-dark">
              {c.getIn(['subject', 'commonName']) || 'Chain Certificate'}
            </span>
          </button>
          <Collapse isOpen={chainIndex === i}>
            <CertificateMetadata certificate={c} />
          </Collapse>
        </div>
      ))}
    </>
  );
};

export const CertificateDetailsModal = ({
  certificate,
  isOpen,
  toggle,
  title,
  downloadFilename,
}) => {
  return (
    certificate && (
      <Modal isOpen={isOpen} size="lg" toggle={toggle}>
        <ModalHeader toggle={toggle}>
          {title || 'Certificate Details'}
        </ModalHeader>
        <ModalBody>
          <CertificateMetadata certificate={certificate} />
          {certificate.get('chain') &&
            certificate.get('chain').size > 0 && (
              <CertificateChainMetadata chain={certificate.get('chain')} />
            )}
          <div className="form-group mt-4">
            <CodeViewer value={certificate.get('pem')} />
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={downloadCertificate(
              certificate.get('pem'),
              downloadFilename || `certificate.crt`,
            )}
          >
            <Icon name="download" />
            <span>Download</span>
          </button>
          <button
            type="button"
            className="btn btn-link-primary"
            onClick={toggle}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    )
  );
};
